import { FatTransactionsPageAllocationTransactionsQuery } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { ALLOCATION_QUERY, EXECUTE_ACTION_MUTATION } from '../queries';
import { AllocationWithUpdated } from '../table/table';

interface MarkAsSentToInvestorProps {
  isOpen: boolean;
  currentRow: AllocationWithUpdated;
  onClose: () => void;
  updateLocalAllocation: (updatedAllocation: FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations'][number]) => void;
  startFlashRowAnimation: (allocationId: string) => void;
  operationWorkflowTransitionTaskSummariesRefetch: () => void;
}

const MarkAsSentToInvestor = ({
  isOpen,
  currentRow,
  onClose,
  updateLocalAllocation,
  startFlashRowAnimation,
  operationWorkflowTransitionTaskSummariesRefetch
}: MarkAsSentToInvestorProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [executeAction] = useMutation(EXECUTE_ACTION_MUTATION);

  const updateAllocationRowData = async () => {
    try {
      const { data: allocationData } = await client.query({
        query: ALLOCATION_QUERY,
        variables: {
          id: currentRow.id
        }
      });

      updateLocalAllocation(allocationData.Allocation);
    } catch (error) {
      console.error(error);
    }
  };

  const onSave = async () => {
    try {
      setErrorMessage(null);
      setLoading(true);
      if (!currentRow?.id) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      await executeAction({
        variables: {
          data: {
            action: 'markAsSentToInvestor',
            allocationId: currentRow.id
          }
        }
      });
      operationWorkflowTransitionTaskSummariesRefetch();
      startFlashRowAnimation(currentRow.id);
      onClose();
      updateAllocationRowData();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent title="Mark as Sent to Investor" isOpen={isOpen} onClose={onClose} confirmButton={{ name: 'Confirm', onClick: onSave }}>
      {loading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <ModalBody>
            <p>Please confirm that the necessary documentation has been delivered to the Investor.</p>
          </ModalBody>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default MarkAsSentToInvestor;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  line-height: 22.4px;
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
